import { useCallback, useMemo, useState, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { useQuestionAndAnswerStore, useRoomConfigStore, useWebinarRoomStore } from '@src/stores';

import SocketService from '@src/services/SocketService';

import { formatTimeStamp } from '@src/utils';

import { queryKeys } from '@src/utils/queryKeys';

import { EQuestionAndAnswerSockets, IQuestionAndAnswerResponse } from '@src/types';

const useAskQuestion = () => {
  const queryClient = useQueryClient();
  const [questionText, setQuestionText] = useState('');
  const [isAskAnonymously, setIsAskAnonymously] = useState(false);

  const { isAskAnonymouslyEnabled } = useRoomConfigStore(
    useShallow((state) => ({
      isAskAnonymouslyEnabled: state.questionsConfig.askAnonymously,
    }))
  );

  const { maxAllowedQuestions, isAskAnonymouslySettings } = useWebinarRoomStore(
    useShallow((state) => ({
      maxAllowedQuestions: state.attendeeDetails?.roomSetting?.questionSettings?.maxQuestions || 1,
      isAskAnonymouslySettings: state.attendeeDetails?.roomSetting?.questionSettings?.allowAnonymous,
    }))
  );

  const {
    myQuestionsLength,

    isQuestionBoxOpened,
    questionToEdit,
    setIsQuestionBoxOpened,
    setQuestionToEdit,
  } = useQuestionAndAnswerStore(
    useShallow((state) => ({
      myQuestionsLength: state.myQuestionsList.length,
      addQuestion: state.addQuestion,
      updateQuestion: state.updateQuestion,
      isQuestionBoxOpened: state.isQuestionBoxOpened,
      questionToEdit: state.questionToEdit,
      setIsQuestionBoxOpened: state.setIsQuestionBoxOpened,
      setQuestionToEdit: state.setQuestionToEdit,
    }))
  );

  const { author, formattedTime } = useMemo(() => {
    const author = questionToEdit?.anonymous ? null : (questionToEdit?.creator?.name ?? null);
    const formattedTime = formatTimeStamp(questionToEdit?.createAt ?? new Date().toISOString());

    return {
      author,
      formattedTime,
    };
  }, [questionToEdit?.anonymous, questionToEdit?.createAt, questionToEdit?.creator?.name]);

  const isAskQuestionDisabled = useMemo(
    () => myQuestionsLength >= maxAllowedQuestions,
    [maxAllowedQuestions, myQuestionsLength]
  );

  const isPostQuestionDisabled = useMemo(() => !questionText.trim().length, [questionText]);

  const isEditQuestionDisabled = useMemo(
    () => questionText.trim() === questionToEdit?.text.trim(),
    [questionText, questionToEdit]
  );

  useEffect(() => {
    if (questionToEdit) {
      setQuestionText(questionToEdit.text);
      setIsAskAnonymously(questionToEdit.anonymous);
    }
  }, [questionToEdit]);

  const handleChangeQuestion = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value;
    setQuestionText(text);
  }, []);

  const handleAnonymouslyCheck = useCallback((isChecked: boolean) => {
    setIsAskAnonymously(isChecked);
  }, []);

  const handleAskQuestionClick = useCallback(() => {
    setIsQuestionBoxOpened(true);
  }, [setIsQuestionBoxOpened]);

  const resetStates = useCallback(() => {
    setQuestionText('');
    setIsAskAnonymously(false);
    setQuestionToEdit(null);
    setIsQuestionBoxOpened(false);
  }, [setQuestionText, setIsAskAnonymously, setQuestionToEdit, setIsQuestionBoxOpened]);

  const handleCloseAskQuestion = useCallback(() => {
    resetStates();
  }, [resetStates]);

  const handlePostQuestion = useCallback(async () => {
    if (questionToEdit) {
      try {
        const response: IQuestionAndAnswerResponse = await SocketService.request(
          EQuestionAndAnswerSockets.EDIT_QUESTION,
          {
            questionId: questionToEdit.id,
            newText: questionText,
          }
        );
        if (!response?.success) {
          console.error('Failed to edit question:', response?.message);
          return;
        }
        queryClient.invalidateQueries({
          queryKey: [queryKeys.webinar.questions],
        });
        resetStates();
        return;
      } catch (error) {
        console.error('Failed to edit question:', error);
      }
    }

    try {
      const response: IQuestionAndAnswerResponse = await SocketService.request(
        EQuestionAndAnswerSockets.SUBMIT_QUESTION,
        {
          text: questionText,
          anonymous: isAskAnonymously,
        }
      );

      const { question, success, message } = response;

      if (!success) {
        console.error('Failed to create question:', message);
        return;
      }

      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.questions],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.webinar.myQuestions],
      });
      // Normalize dates (guard against invalid values)
      question.createAt = new Date(question.createAt).toISOString();
      question.updateAt = new Date(question.updateAt).toISOString();

      resetStates();
    } catch (error) {
      console.error('Socket error while creating question:', error);
    }
  }, [questionText, isAskAnonymously, resetStates, questionToEdit, queryClient]);

  const handleQuestionKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (!isPostQuestionDisabled) {
          handlePostQuestion();
        }
      }
    },
    [handlePostQuestion, isPostQuestionDisabled]
  );

  return {
    questionText,
    isAskAnonymously,
    isQuestionBoxOpened,
    isAskQuestionDisabled,
    isPostQuestionDisabled,
    isEditQuestionDisabled,
    questionToEdit,
    setQuestionToEdit,
    isAskAnonymouslyEnabled,
    author,
    formattedTime,
    maxAllowedQuestions,
    handlePostQuestion,
    handleChangeQuestion,
    handleQuestionKeyDown,
    handleAnonymouslyCheck,
    handleCloseAskQuestion,
    handleAskQuestionClick,
    isAskAnonymouslySettings,
  };
};

export default useAskQuestion;
